import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../../../../../../../../theme/ui/Atoms/Modal/Dialog/Dialog';
import Input from '../../../../../../../../theme/ui/Atoms/Input/Input';
import useTypeEntity from '../../../useTypeEntity';
import {
  DEFAULT_CURRENCY,
  getCostPerKwhUnitSettingScope,
  IUseCostPerKwhSettingProps,
  useCostPerKwhSetting,
} from './useCostPerKwhSetting';
import { Typography } from '@mui/material';
import GroupAPI from '../../../../../../../../service/group/GroupAPI';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useErrorMessage from '../../../../../../../../shared/hooks/useErrorMessage';
import WorkspaceAPI from '../../../../../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import useMyWorkspace from '../../../../../../../../redux/workspace/hooks/useMyWorkspace';

export interface UpdateEnergyCostByGroupModalProps
  extends IUseCostPerKwhSettingProps {
  open: boolean;
  onClose: () => void;
}
const validationSchema = yup.object({
  attributes: yup.object({
    reportPowerPricePerKilowattHour: yup
      .number()
      .typeError('Must be a number')
      .min(0, 'Must be greater than or equal to 0')
      .nullable(),
  }),
});

const UpdateEnergyCostByGroupModal: React.FC<
  UpdateEnergyCostByGroupModalProps
> = ({ open, onClose, ...props }) => {
  const { workspaceId } = props;
  const { t } = useTranslation();
  const { entity } = useTypeEntity();
  const {
    value,
    unitString,
    group: fatherGroup,
    workspace,
    initialValue,
    inheritValue,
  } = useCostPerKwhSetting(props);

  const { data: workspaceSelected } = WorkspaceAPI.useGetWorkspaceByIdQuery(
    workspaceId ?? skipToken
  );
  const { data: myWorkspace, refetch: refetchMyWorkspace } = useMyWorkspace();

  const [updateGroup, { isLoading }] =
    GroupAPI.endpoints.updateGroup.useMutation();

  const [updateWorkspace, { isLoading: isUpdatingWorkspace }] =
    WorkspaceAPI.endpoints.updateSettings.useMutation();

  const initialValues = useMemo(() => {
    return {
      attributes: {
        reportPowerPricePerKilowattHour: initialValue ?? '',
        reportPowerPriceCurrency: DEFAULT_CURRENCY,
      },
    };
  }, [initialValue]);

  const errorMessage = useErrorMessage();

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      switch (props.typeRequest) {
        case 'group': {
          if (!entity)
            return errorMessage('Update not supported for this group');
          const updatedAttributes = values.attributes
            ?.reportPowerPricePerKilowattHour
            ? {
                ...entity.attributes,
                reportPowerPricePerKilowattHour:
                  values.attributes?.reportPowerPricePerKilowattHour + '',
                reportPowerPriceCurrency: DEFAULT_CURRENCY,
              }
            : {
                ...entity.attributes,
                reportPowerPricePerKilowattHour: undefined,
                reportPowerPriceCurrency: undefined,
              };

          return updateGroup({
            id: entity.uuid,
            data: {
              id: entity.id,
              attributes: updatedAttributes,
            },
          })
            .unwrap()
            .then(() => {
              onClose();
            })
            .catch(errorMessage);
        }
        case 'workspace': {
          if (!workspaceSelected)
            return errorMessage('Update not supported for this workspace');
          const updatedAttributes = values.attributes
            ?.reportPowerPricePerKilowattHour
            ? {
                ...workspaceSelected.settings,
                reportPowerPricePerKilowattHour:
                  values.attributes?.reportPowerPricePerKilowattHour + '',
                reportPowerPriceCurrency: DEFAULT_CURRENCY,
              }
            : {
                ...workspaceSelected.settings,
                reportPowerPricePerKilowattHour: null,
                reportPowerPriceCurrency: null,
              };
          return updateWorkspace({
            formData: updatedAttributes,
            workspaceId: workspaceSelected.id,
          })
            .unwrap()
            .then(() => onClose())
            .catch(errorMessage);
        }
        case 'user':
        case 'workspaces': {
          if (!myWorkspace)
            return errorMessage(
              'Update not supported for user/workspaces level'
            );
          const updatedAttributes = values.attributes
            ?.reportPowerPricePerKilowattHour
            ? {
                ...myWorkspace.settings,
                reportPowerPricePerKilowattHour:
                  values.attributes?.reportPowerPricePerKilowattHour + '',
                reportPowerPriceCurrency: DEFAULT_CURRENCY,
              }
            : {
                ...myWorkspace.settings,
                reportPowerPricePerKilowattHour: undefined,
                reportPowerPriceCurrency: undefined,
              };
          return updateWorkspace({
            formData: updatedAttributes,
            workspaceId: myWorkspace.id,
          })
            .unwrap()
            .then(() => refetchMyWorkspace())
            .then(() => onClose())
            .catch(errorMessage);
        }
      }
    },
  });
  // Save handler for Dialog's onSave
  const handleSave = () => {
    formik.handleSubmit();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      onSave={handleSave}
      title={t('dashboard.energy_cost.edit_cost_per_kwh') ?? ''}
      maxWidth="xs"
      loading={formik.isSubmitting || isLoading || isUpdatingWorkspace}
      fullWidth
    >
      {fatherGroup && inheritValue && (
        <Typography gutterBottom>
          {t('dashboard.energy_cost.default_value_explanation', {
            defaultValue: value,
            unit: unitString,
            groupName: fatherGroup?.name ?? '',
          })}
        </Typography>
      )}
      {workspace && inheritValue && (
        <Typography gutterBottom>
          {t('dashboard.energy_cost.default_value_explanation', {
            defaultValue: value,
            unit: unitString,
            groupName: workspace?.name ?? '',
          })}
        </Typography>
      )}
      <Input
        autoFocus
        label={t('dashboard.energy_cost.cost_per_kwh_label')}
        id="attributes.reportPowerPricePerKilowattHour"
        name="attributes.reportPowerPricePerKilowattHour"
        type="number"
        fullWidth
        value={formik.values.attributes?.reportPowerPricePerKilowattHour ?? ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(
          formik.errors.attributes?.reportPowerPricePerKilowattHour
        )}
        helperText={formik.errors.attributes?.reportPowerPricePerKilowattHour}
        startAdornment={
          <Typography mr={1}>
            {t(
              `dashboard.energy_cost.currencies.${
                getCostPerKwhUnitSettingScope(entity) ?? DEFAULT_CURRENCY
              }`
            )}
          </Typography>
        }
        inputProps={{ min: 0, step: 0.01 }}
      />
    </Dialog>
  );
};

export default UpdateEnergyCostByGroupModal;
